import type { LearnTopic } from '../types';
import { $, payoffPerShare } from '../format';

export const riskGraphTopic: LearnTopic = {
  id: 'risk-graph',
  tier: 'basics',
  title: 'Reading a risk graph',
  summary:
    'Stock price along the bottom, profit up the side. Pick a price, go up to the line, read the money.',
  body: [
    'A risk graph shows the whole outcome of a position at once. The stock price runs along the bottom; profit and loss run up the side, with zero somewhere in the middle. The line is what the position is worth at expiration at every price the stock could be.',
    'Reading one is mechanical. Pick a price along the bottom, go straight up to the line, then read the money off the side. That is what the position makes or loses if the stock finishes there. No prediction is involved — the graph answers "what if", one price at a time.',
    'Break-even is where the line crosses zero. An iron condor has one on each side; a single credit spread has one, on the side it was sold. Either way the break-even sits a credit further out than the short strike, because the credit has to be eaten through before you are actually down — which is why a stock can push slightly past a short strike without the trade being a loser yet.',
    'The shape of the line is the strategy. Owning shares is a single straight diagonal — every dollar the stock moves is a dollar to you. Options bend that line and flatten it. On a position with wings — the options bought beyond each short strike — the two ends are flat: past those strikes the loss stops growing, and the flat stretches are exactly where the wings have done their job.',
    "Steward's Risk view draws this line for each open position, with the live price marked on it, so the graph in your head and the numbers on the row are the same thing. The buyers-and-sellers examples and the lessons behind them are Howard's.",
  ],
  explain(ctx) {
    const { symbol, price } = ctx;
    const closing = 'That is the expiration figure: until then the position is worth whatever it costs to buy back.';

    // Two separate fallbacks, because the two nulls mean different things: no quote means the
    // point along the bottom is unknown, no credit means the line has no height to measure from.
    if (price == null) {
      return [`There is no live price for ${symbol} right now, so there is no point on the line to read.`, closing];
    }
    const payoff = payoffPerShare(ctx);
    if (payoff == null) {
      return ['No credit is recorded for this position, so the line cannot be placed against zero.', closing];
    }

    const whereIsIt = payoff > 0 ? 'above zero' : payoff < 0 ? 'below zero' : 'right on zero';
    return [
      `At ${$(price)} the line sits ${whereIsIt}: ${$(payoff)} a share if ${symbol} finished right here.`,
      closing,
    ];
  },
  appliesTo() {
    return true;
  },
};
